import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import { api } from "../../../../services/api/api.service";
import {
    IDartsShootOutGames,
    IDartsShootOutGamesReducer,
    IGetShootoutGamesResponse,
    IShootOutGameEditPayload,
    IShootOutGamePayload
} from "./types";
import { API_DARTS_GAMES_INIT_DATA, API_DARTS_SHOOTOUT_GAMES } from "../../../../services/api/api.urls";
import { toastr } from "react-redux-toastr";
import { IGamesInitData } from "../../../../pages/Darts/Games/DartsGamesCRUD.interface";
import { COMPANY_ABBR } from "../../../../common/constants/company";
import { TryCatch } from "../../../../services/TryCatch";

const initialState: IDartsShootOutGamesReducer = {
    shootOutGames: [] as IDartsShootOutGames[],
    initData: {} as IGamesInitData,
    editGameId: 0,
    filteredGames: [],
    editMode: false,
    totalItems: 0,
    totalPages: 0,
    pageNum: 1,
    pageSize: 10,
}

export const getShootOutDartsInit = createAsyncThunk<IGamesInitData, void, { rejectValue: string }>(
    'cmsShootOutDartsGames/get',
    async (_, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(API_DARTS_GAMES_INIT_DATA).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, result.error);
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getDartsShootOutGames = createAsyncThunk<IGetShootoutGamesResponse, string, { rejectValue: string }>(
    'getDartsShootOutGames/get',
    async (query, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_DARTS_SHOOTOUT_GAMES}?${query}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, result.error);
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)


export const createDartsShootOutGame = createAsyncThunk<IDartsShootOutGames, IShootOutGamePayload, { rejectValue: string }>(
    'getDartsShootOutGames/post',
    async (payload, {rejectWithValue}) => {
        let status = 0;
        const result = await TryCatch(
            () => api.post(API_DARTS_SHOOTOUT_GAMES, payload).then(res => { status = res.status; return res.data; })
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to create game.");
            return rejectWithValue(result.error);
        }
        if (status === 200 || status === 204) {
            toastr.warning(COMPANY_ABBR, "This game is already exist.");
            //    return rejectWithValue("This game is already exist");
        }
        if (status === 201) {
            toastr.success(COMPANY_ABBR, "Darts game has been created.");
        }
        return result.data;
    }
)


export const editDartsShootOutGame = createAsyncThunk<IDartsShootOutGames, IShootOutGameEditPayload, { rejectValue: string }>(
    'cmsShootOutDartsGames/edit',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_DARTS_SHOOTOUT_GAMES}`, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to edit game.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts game has been edited.");
        return result.data;
    }
)

export const deleteShootOutGame = createAsyncThunk<IDartsShootOutGames, number, { rejectValue: string }>(
    'cmsShootOutDartsGames/delete',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_DARTS_SHOOTOUT_GAMES}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to delete game.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts game has been deleted.");
        return result.data;
    }
)


export const cmsShootOutDartsGamesSlice = createSlice({
    name: 'cmsShootOutDartsGames',
    reducers: {
        setShootOutGameEditMode: (state, action: PayloadAction<boolean>) => {
            state.editMode = action.payload
        },
        setPageNumber: (state, action) => {
            state.pageNum = action.payload
        },
        changePageSize: (state, action) => {
            state.pageSize = action.payload
        },
        setEditGameId: (state, action) => {
            state.editGameId = action.payload
            state.editMode = true
        }
    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getDartsShootOutGames.fulfilled, (state: IDartsShootOutGamesReducer, action) => {
                state.shootOutGames = action.payload.items
                state.totalPages = action.payload.totalPages
            })
            .addCase(getShootOutDartsInit.fulfilled, (state: IDartsShootOutGamesReducer, action) => {
                state.initData = action.payload;
            })
            .addCase(deleteShootOutGame.fulfilled, (state: IDartsShootOutGamesReducer, action) => {
                state.shootOutGames = state.shootOutGames.filter(
                    (game) => game.id !== action.meta.arg
                );
            })
            .addCase(createDartsShootOutGame.fulfilled, (state: IDartsShootOutGamesReducer, action) => {
                state.shootOutGames = state.shootOutGames.concat(action.payload);
            })

            .addCase(editDartsShootOutGame.fulfilled, (state: IDartsShootOutGamesReducer, action) => {
                state.shootOutGames = state.shootOutGames.map((game) => {
                    if (game.id === action.payload.id) {
                        game = action.payload
                    }
                    return game
                })
            })
    },
});

export const {setShootOutGameEditMode, setPageNumber, setEditGameId,changePageSize} = cmsShootOutDartsGamesSlice.actions
export default cmsShootOutDartsGamesSlice.reducer;

